/**
 * Pathfinder GPS Cleanup Tool (gis-admin only)
 *
 * Surgical, polygon-scoped soft/hard delete of GPS noise. Used to clean
 * up positioning-fallback artifacts that the always-on map filters
 * can't fully handle - the TPA/PIE/MacDill phantom-dwell triangle and
 * its kin, where a stationary device near a GPS-denied zone produces
 * fake tracks and dwells at distant Wi-Fi-dense anchors.
 *
 * Two-component design (forced by react-map-gl):
 *
 *   <PathfinderCleanupDrawControl />  - small wrapper around MapboxDraw,
 *                                       MUST live inside the <Map> tree.
 *   <PathfinderCleanupPanel />        - the side panel with PIN, preview,
 *                                       and action buttons. Lives outside
 *                                       the map so it can scroll, take
 *                                       focus, and not interact with the
 *                                       map's pointer events.
 *
 * State (PIN, drawn polygon, scope toggles) is held in PathfinderMapGL
 * and passed to both components. The two communicate only via that
 * parent state.
 *
 * Layered access control:
 *   1. The toolbar button is gated on Azure AD gis-admin group
 *      membership (object id 77924e41-ac40-4aa4-87a5-7b4cc5c18f2a /
 *      role gis_admin) in PathfinderMapGL — not general administrator.
 *   2. The first interaction is a PIN modal. Without a verified PIN
 *      the panel won't make any data calls.
 *   3. Every backend endpoint independently verifies gis_admin
 *      AND the per-request X-Cleanup-PIN header.
 *   4. Every action writes to admin_cleanup_audit on the server,
 *      preserving the rows' JSONB snapshots so even hard deletes
 *      can be reversed by SQL within the retention window.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
  alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import LockIcon from '@mui/icons-material/Lock';
import EditLocationAltIcon from '@mui/icons-material/EditLocationAlt';
import RemoveIcon from '@mui/icons-material/Remove';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useControl } from 'react-map-gl';
// @ts-ignore - mapbox-gl-draw types are not bundled with the package
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import { pathfinderApi } from '../../services/pathfinderApi';

// =========================================================================
// Draw control (lives INSIDE <Map>)
// =========================================================================

interface PathfinderCleanupDrawControlProps {
  /** When false, the control mounts but stays in simple_select mode. */
  active: boolean;
  /** Called when a polygon is created OR updated. */
  onPolygonChange: (geojson: any | null) => void;
  /**
   * Counter that, when incremented, wipes the current polygon and
   * re-enters draw_polygon so the analyst can immediately start a new
   * one. Used after a successful delete so they don't have to close
   * and reopen the panel to draw again.
   */
  resetSignal?: number;
}

/**
 * MapboxDraw wrapper specific to the cleanup tool. The control is
 * always mounted (cheap), but only activates draw_polygon when the
 * `active` flag is true. When the tool closes we wipe the canvas so
 * no half-drawn polygon hangs around.
 */
export const PathfinderCleanupDrawControl: React.FC<PathfinderCleanupDrawControlProps> = ({
  active,
  onPolygonChange,
  resetSignal,
}) => {
  // We hold the latest callback in a ref so we don't have to re-create
  // the MapboxDraw instance every render. react-map-gl's useControl
  // is touchy about reference identity.
  const callbackRef = useRef(onPolygonChange);
  callbackRef.current = onPolygonChange;

  // @ts-ignore - MapboxDraw control type compatibility with react-map-gl
  const draw = useControl<any>(
    () =>
      new MapboxDraw({
        displayControlsDefault: false,
        controls: { polygon: false, trash: false }, // we drive mode programmatically
        defaultMode: 'simple_select',
        styles: [
          // Distinct red-on-red so the cleanup polygon doesn't get
          // confused with a co-traveler route or a geofence polygon.
          {
            id: 'cleanup-poly-fill-inactive',
            type: 'fill',
            filter: ['all', ['==', 'active', 'false'], ['==', '$type', 'Polygon']],
            paint: { 'fill-color': '#ff1744', 'fill-opacity': 0.12 },
          },
          {
            id: 'cleanup-poly-fill-active',
            type: 'fill',
            filter: ['all', ['==', 'active', 'true'], ['==', '$type', 'Polygon']],
            paint: { 'fill-color': '#ff1744', 'fill-opacity': 0.22 },
          },
          {
            id: 'cleanup-poly-stroke',
            type: 'line',
            filter: ['all', ['==', '$type', 'Polygon']],
            layout: { 'line-cap': 'round', 'line-join': 'round' },
            paint: { 'line-color': '#ff1744', 'line-width': 2, 'line-dasharray': [2, 1] },
          },
          {
            id: 'cleanup-vertex-halo',
            type: 'circle',
            filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point']],
            paint: { 'circle-radius': 6, 'circle-color': '#ffffff' },
          },
          {
            id: 'cleanup-vertex',
            type: 'circle',
            filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point']],
            paint: { 'circle-radius': 4, 'circle-color': '#ff1744' },
          },
          {
            id: 'cleanup-midpoint',
            type: 'circle',
            filter: ['all', ['==', 'meta', 'midpoint'], ['==', '$type', 'Point']],
            paint: { 'circle-radius': 3, 'circle-color': '#ff1744' },
          },
        ],
      }),
    ({ map }) => {
      const handleCreate = (e: any) => {
        // MapboxDraw allows multiple polygons; we only want one at a time
        // for cleanup, so keep just the most recent.
        const all = (draw as any)?.getAll?.()?.features || [];
        if (all.length > 1) {
          const keep = e.features[0];
          (draw as any).deleteAll();
          (draw as any).add(keep);
        }
        callbackRef.current(e.features[0]);
      };
      const handleUpdate = (e: any) => callbackRef.current(e.features[0]);
      const handleDelete = () => callbackRef.current(null);
      map.on('draw.create', handleCreate);
      map.on('draw.update', handleUpdate);
      map.on('draw.delete', handleDelete);
      (map as any).__cleanupDrawHandlers = { handleCreate, handleUpdate, handleDelete };
    },
    ({ map }) => {
      const h = (map as any).__cleanupDrawHandlers;
      if (h) {
        map.off('draw.create', h.handleCreate);
        map.off('draw.update', h.handleUpdate);
        map.off('draw.delete', h.handleDelete);
      }
    },
    { position: 'top-right' },
  );

  // Drive draw mode from the `active` prop. Activating switches into
  // draw_polygon (so the user can immediately click to add vertices);
  // deactivating wipes any in-progress polygon.
  useEffect(() => {
    if (!draw) return;
    if (active) {
      try { (draw as any).changeMode('draw_polygon'); } catch {/* drawing already in progress */}
    } else {
      try {
        (draw as any).deleteAll();
        (draw as any).changeMode('simple_select');
      } catch {/* control not yet ready */}
      callbackRef.current(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, draw]);

  // Reset signal: wipe whatever's drawn and drop the user back into
  // draw_polygon so they can immediately start a new selection. Skip
  // the initial mount (resetSignal === 0 / undefined) so we don't fight
  // the active-mode effect above.
  const firstResetRef = useRef(true);
  useEffect(() => {
    if (firstResetRef.current) {
      firstResetRef.current = false;
      return;
    }
    if (!draw || !active) return;
    try {
      (draw as any).deleteAll();
      (draw as any).changeMode('draw_polygon');
    } catch {/* control not yet ready */}
    callbackRef.current(null);
  }, [resetSignal, draw, active]);

  return null;
};

// =========================================================================
// Panel (lives OUTSIDE <Map>)
// =========================================================================

interface PathfinderCleanupPanelProps {
  open: boolean;
  onClose: () => void;
  /** The polygon currently drawn on the map, or null. */
  polygon: any | null;
  /** Time window the cleanup applies to. */
  startDate: Date;
  endDate: Date;
  /** Entities currently visible on the map (used as scope-filter options). */
  availableEntityIds: string[];
  /** Called after a successful quarantine/delete so the map can refetch. */
  onCleanupComplete?: () => void;
  /**
   * Called after a successful delete to wipe the drawn polygon and
   * re-enter draw_polygon mode. Lets the analyst keep cleaning a second
   * area without closing the panel (which would also drop their PIN).
   */
  onResetPolygon?: () => void;
  /**
   * Bubbles PIN-verified state up so the parent can keep the draw
   * control inactive until PIN is good. Without this, an admin could
   * close + reopen the panel and start drawing again before re-entering
   * the PIN (the API would still reject without a valid PIN, but it
   * looks confusing on screen).
   */
  onPinVerifiedChange?: (verified: boolean) => void;
}

type Scope = 'all' | 'subset';

interface PreviewResult {
  tracks?: { count: number; by_entity: Array<{ entity_id: string; count: number }>; sample: any[] };
  dwells?: { count: number; by_entity: Array<{ entity_id: string; count: number }>; sample: any[] };
}

export const PathfinderCleanupPanel: React.FC<PathfinderCleanupPanelProps> = ({
  open,
  onClose,
  polygon,
  startDate,
  endDate,
  availableEntityIds,
  onCleanupComplete,
  onResetPolygon,
  onPinVerifiedChange,
}) => {
  const theme = useTheme();

  // PIN held in component state only. Lost on refresh, which is the point.
  const [pin, setPin] = useState<string>('');
  const [pinVerified, setPinVerified] = useState<boolean>(false);
  const [pinError, setPinError] = useState<string | null>(null);
  const [pinLoading, setPinLoading] = useState<boolean>(false);

  // Collapsed = panel shrinks to just the header bar so the user can see
  // the map underneath while drawing / adjusting the polygon. We don't
  // close the panel because that resets PIN verification.
  const [collapsed, setCollapsed] = useState<boolean>(false);

  // Default scope to 'subset' (loaded entities only) so a casual click
  // on Delete doesn't wipe data for entities the analyst can't even
  // see. The 'all' option remains available, but it's now gated by a
  // visible warning band. This was a hard lesson: an unscoped polygon
  // on a busy area can match 70k+ tracks across the entire DB even
  // though only ~300 are visible for the currently selected entity.
  const [scope, setScope] = useState<Scope>('subset');
  const [selectedEntityIds, setSelectedEntityIds] = useState<string[]>([]);

  // Whenever the parent's visible entity list changes (entity toggle,
  // date filter, etc.), preselect all of them. Analyst can still
  // deselect individual ones before pulling the trigger.
  useEffect(() => {
    setSelectedEntityIds(availableEntityIds);
  }, [availableEntityIds]);

  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [previewLoading, setPreviewLoading] = useState<boolean>(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const [actionResult, setActionResult] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Hard-delete confirmation dialog
  const [hardDeleteOpen, setHardDeleteOpen] = useState<false | 'tracks' | 'dwells'>(false);
  const [hardDeleteConfirm, setHardDeleteConfirm] = useState<string>('');

  const [reason, setReason] = useState<string>('');

  // Mirror PIN-verified state to the parent so it can keep the draw
  // control inactive until the analyst is unlocked.
  useEffect(() => {
    onPinVerifiedChange?.(pinVerified);
  }, [pinVerified, onPinVerifiedChange]);

  // Reset everything when the panel closes
  useEffect(() => {
    if (!open) {
      setPin('');
      setPinVerified(false);
      setPinError(null);
      setPreview(null);
      setPreviewError(null);
      setActionResult(null);
      setActionError(null);
      setHardDeleteOpen(false);
      setHardDeleteConfirm('');
      setReason('');
      setSelectedEntityIds([]);
      setScope('subset');
    }
  }, [open]);

  // Auto-clear preview when the polygon changes (the previous counts no longer apply).
  useEffect(() => {
    setPreview(null);
    setPreviewError(null);
    setActionResult(null);
  }, [polygon]);

  const effectiveEntityIds = useMemo(() => {
    if (scope === 'all') return undefined;
    return selectedEntityIds.length > 0 ? selectedEntityIds : undefined;
  }, [scope, selectedEntityIds]);

  const handleVerifyPin = useCallback(async () => {
    if (!pin) {
      setPinError('Enter a PIN.');
      return;
    }
    setPinLoading(true);
    setPinError(null);
    try {
      await pathfinderApi.cleanupVerifyPin(pin);
      setPinVerified(true);
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message || 'PIN verification failed';
      setPinError(detail);
      setPinVerified(false);
    } finally {
      setPinLoading(false);
    }
  }, [pin]);

  const handlePreview = useCallback(async () => {
    if (!polygon || !pinVerified) return;
    setPreviewLoading(true);
    setPreviewError(null);
    setActionResult(null);
    try {
      const result = await pathfinderApi.cleanupPreview({
        pin,
        polygon: polygon.geometry || polygon, // accept either Feature or Geometry
        timeRange: { start: startDate, end: endDate },
        entityIds: effectiveEntityIds,
        target: 'both',
      });
      setPreview(result);
    } catch (e: any) {
      setPreviewError(e?.response?.data?.detail || e?.message || 'Preview failed');
    } finally {
      setPreviewLoading(false);
    }
  }, [polygon, pin, pinVerified, startDate, endDate, effectiveEntityIds]);

  // Auto-run Preview whenever the user finishes drawing or moves the
  // polygon. Debounced so dragging a vertex doesn't fire a request per
  // pixel. The Delete buttons stay disabled until the preview returns
  // with a count > 0, so this is what makes them enable on their own
  // once the polygon contains something - no manual Preview click
  // required.
  useEffect(() => {
    if (!polygon || !pinVerified) return;
    const handle = setTimeout(() => {
      handlePreview();
    }, 450);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polygon, pinVerified, scope, selectedEntityIds]);

  const handleHardDelete = useCallback(async () => {
    if (!polygon || !pinVerified || !hardDeleteOpen) return;
    if (hardDeleteConfirm !== 'DELETE') {
      setActionError('Type DELETE (uppercase) to confirm.');
      return;
    }
    setActionLoading(true);
    setActionError(null);
    setActionResult(null);
    try {
      const result = await pathfinderApi.cleanupHardDelete({
        pin,
        target: hardDeleteOpen,
        polygon: polygon.geometry || polygon,
        timeRange: { start: startDate, end: endDate },
        entityIds: effectiveEntityIds,
        reason: reason || undefined,
        confirmText: hardDeleteConfirm,
      });
      setActionResult(
        `Permanently deleted ${result.affected_count} ${hardDeleteOpen}.` +
          (result.audit_id ? ` Audit #${result.audit_id} retains a JSONB snapshot.` : ''),
      );
      setPreview(null);
      setHardDeleteOpen(false);
      setHardDeleteConfirm('');
      onCleanupComplete?.();
      // Wipe the polygon and drop straight back into draw mode so the
      // analyst can keep cleaning a second region without losing their
      // PIN session.
      onResetPolygon?.();
    } catch (e: any) {
      setActionError(e?.response?.data?.detail || e?.message || 'Delete failed');
    } finally {
      setActionLoading(false);
    }
  }, [polygon, pin, pinVerified, startDate, endDate, effectiveEntityIds, reason, hardDeleteOpen, hardDeleteConfirm, onCleanupComplete, onResetPolygon]);

  if (!open) return null;

  return (
    <>
      <Box
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          // Slimmer panel - 320 instead of 380 - so it covers less map.
          // Combined with the minimize button (header-only collapse),
          // this gives the admin two ways to see more of the map while
          // adjusting the polygon: collapse, or just glance through the
          // semi-transparent backdrop.
          width: 320,
          maxHeight: 'calc(100vh - 32px)',
          overflowY: collapsed ? 'hidden' : 'auto',
          // Slightly more transparent (0.92 -> 0.88) when expanded so the
          // map underneath stays partially visible. When collapsed we
          // bump opacity up because the bar is small enough that it
          // doesn't matter.
          backgroundColor: alpha(theme.palette.background.paper, collapsed ? 0.95 : 0.88),
          backdropFilter: 'blur(6px)',
          border: `1px solid ${alpha(theme.palette.error.main, 0.5)}`,
          borderRadius: 1.5,
          boxShadow: theme.shadows[10],
          zIndex: 1300,
          p: collapsed ? 1 : 1.5,
          transition: 'width 120ms ease, padding 120ms ease, opacity 120ms ease',
        }}
      >
        {/* Header. When collapsed, this row is the entire panel - the
            user can see the full map underneath and click the expand
            icon to come back. */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: collapsed ? 0 : 1 }}>
          <DeleteIcon sx={{ color: theme.palette.error.main }} fontSize="small" />
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, flex: 1, fontSize: '0.85rem' }}
          >
            GPS Cleanup (Admin)
          </Typography>
          <Tooltip title={collapsed ? 'Expand panel' : 'Minimize panel (keeps PIN session)'}>
            <IconButton size="small" onClick={() => setCollapsed((v) => !v)}>
              {collapsed ? <ExpandLessIcon fontSize="small" /> : <RemoveIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        {collapsed ? null : (
          <>
        {/* PIN entry */}
        {!pinVerified ? (
          <Box sx={{ mb: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <LockIcon fontSize="small" />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Enter cleanup PIN
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1}>
              <TextField
                type="password"
                size="small"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                placeholder="PIN"
                fullWidth
                inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code', maxLength: 32 }}
                onKeyDown={(e) => { if (e.key === 'Enter') handleVerifyPin(); }}
                error={!!pinError}
              />
              <Button
                variant="contained"
                color="primary"
                size="small"
                disabled={pinLoading || !pin}
                onClick={handleVerifyPin}
              >
                {pinLoading ? <CircularProgress size={16} /> : 'Unlock'}
              </Button>
            </Stack>
            {pinError && (
              <Alert severity="error" sx={{ mt: 1, py: 0.5 }}>{pinError}</Alert>
            )}
          </Box>
        ) : (
          <>
            {/* Time scope (display only) */}
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Time range
              </Typography>
              <Typography variant="body2">
                {startDate.toLocaleString()} → {endDate.toLocaleString()}
              </Typography>
            </Box>

            {/* Entity scope. Wording is deliberately blunt - "All
                entities in DB" vs "Only loaded entities" - because
                the prior copy ("All entities in polygon") was easy
                to misread as "everything you can see", when it
                actually meant "every device that has ever passed
                through this polygon, including ones you don't have
                loaded right now". */}
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Entity scope
              </Typography>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                <Chip
                  size="small"
                  label={`Only loaded (${availableEntityIds.length})`}
                  color={scope === 'subset' ? 'primary' : 'default'}
                  onClick={() => setScope('subset')}
                  variant={scope === 'subset' ? 'filled' : 'outlined'}
                />
                <Chip
                  size="small"
                  label="EVERY entity in DB"
                  color={scope === 'all' ? 'warning' : 'default'}
                  onClick={() => setScope('all')}
                  variant={scope === 'all' ? 'filled' : 'outlined'}
                />
              </Stack>
              {scope === 'all' && (
                <Typography
                  variant="caption"
                  color="warning.main"
                  sx={{ display: 'block', mt: 0.5, fontWeight: 600 }}
                >
                  Hits every device in the DB inside the polygon.
                </Typography>
              )}
              {scope === 'subset' && (
                <Box sx={{ mt: 1, maxHeight: 100, overflowY: 'auto', display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {availableEntityIds.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">
                      No entities loaded on the map.
                    </Typography>
                  ) : (
                    availableEntityIds.map((eid) => {
                      const isSel = selectedEntityIds.includes(eid);
                      return (
                        <Chip
                          key={eid}
                          size="small"
                          label={eid}
                          color={isSel ? 'primary' : 'default'}
                          variant={isSel ? 'filled' : 'outlined'}
                          onClick={() => {
                            setSelectedEntityIds((prev) =>
                              prev.includes(eid) ? prev.filter((x) => x !== eid) : [...prev, eid],
                            );
                          }}
                        />
                      );
                    })
                  )}
                </Box>
              )}
            </Box>

            {/* Polygon status. Preview auto-runs (debounced) on
                draw/edit, so no manual button is needed - just show
                the current state and a tiny spinner while loading. */}
            <Divider sx={{ my: 1 }} />
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <EditLocationAltIcon fontSize="small" color={polygon ? 'success' : 'disabled'} />
              <Typography variant="body2" sx={{ flex: 1, color: polygon ? 'text.primary' : 'text.secondary' }}>
                {polygon ? 'Polygon drawn' : 'Click on map to start drawing'}
              </Typography>
              {previewLoading && <CircularProgress size={14} />}
            </Stack>

            {previewError && (
              <Alert severity="error" sx={{ mb: 1, py: 0.5 }}>{previewError}</Alert>
            )}

            {/* Preview results */}
            {preview && (
              <Box
                sx={{
                  mb: 1.5,
                  p: 1,
                  borderRadius: 1,
                  backgroundColor: alpha(theme.palette.warning.main, 0.08),
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  In selection
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 0.5 }}>
                  <Chip size="small" label={`${preview.tracks?.count ?? 0} tracks`} />
                  <Chip size="small" label={`${preview.dwells?.count ?? 0} dwells`} />
                </Stack>
                {preview.tracks?.by_entity && preview.tracks.by_entity.length > 0 && (
                  <Box sx={{ mt: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">By entity (tracks):</Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      {preview.tracks.by_entity.slice(0, 5).map((b) => `${b.entity_id}:${b.count}`).join(' · ')}
                      {preview.tracks.by_entity.length > 5 && ` + ${preview.tracks.by_entity.length - 5} more`}
                    </Typography>
                  </Box>
                )}
                {preview.dwells?.by_entity && preview.dwells.by_entity.length > 0 && (
                  <Box sx={{ mt: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">By entity (dwells):</Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      {preview.dwells.by_entity.slice(0, 5).map((b) => `${b.entity_id}:${b.count}`).join(' · ')}
                      {preview.dwells.by_entity.length > 5 && ` + ${preview.dwells.by_entity.length - 5} more`}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Reason */}
            <TextField
              size="small"
              fullWidth
              placeholder="Reason (e.g. TPA/PIE/MacDill phantom triangle)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              sx={{ mb: 1 }}
            />

            {/* Delete actions. Hard delete only - the row is removed
                from device_tracks / device_dwells outright, so every
                downstream query (POL, COP, AreaSearch, embeddings,
                Story Mode, exports) stops seeing it without any extra
                patching. The JSONB row payload is preserved in
                admin_cleanup_audit so SQL-based restore is still
                possible, but it's a manual operation if you need it. */}
            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
              <Button
                fullWidth
                size="small"
                variant="contained"
                color="error"
                startIcon={<DeleteForeverIcon />}
                disabled={!preview || (preview.tracks?.count ?? 0) === 0 || actionLoading}
                onClick={() => setHardDeleteOpen('tracks')}
                sx={{ flex: 1 }}
              >
                Delete tracks
              </Button>
              <Button
                fullWidth
                size="small"
                variant="contained"
                color="error"
                startIcon={<DeleteForeverIcon />}
                disabled={!preview || (preview.dwells?.count ?? 0) === 0 || actionLoading}
                onClick={() => setHardDeleteOpen('dwells')}
                sx={{ flex: 1 }}
              >
                Delete dwells
              </Button>
            </Stack>

            {actionError && (
              <Alert severity="error" sx={{ mb: 1, py: 0.5 }}>{actionError}</Alert>
            )}
            {actionResult && (
              <Alert severity="success" sx={{ mb: 1, py: 0.5 }}>{actionResult}</Alert>
            )}
          </>
        )}
          </>
        )}
      </Box>

      {/* Hard-delete confirmation dialog */}
      <Dialog
        open={!!hardDeleteOpen}
        onClose={() => { setHardDeleteOpen(false); setHardDeleteConfirm(''); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteForeverIcon color="error" />
          Permanently delete {hardDeleteOpen}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1 }}>
            This will <strong>DELETE</strong>{' '}
            <strong>
              {hardDeleteOpen === 'tracks' && preview?.tracks?.count?.toLocaleString()}
              {hardDeleteOpen === 'dwells' && preview?.dwells?.count?.toLocaleString()}
            </strong>{' '}
            {hardDeleteOpen} from the database. The JSONB payload is preserved in the audit log,
            so an SQL restore is possible within the retention window — but the rows will get
            new primary keys on restore, breaking any foreign-key references.
          </DialogContentText>

          {/* Scope summary. Loud, because a missed scope is how you
              accidentally delete 74k rows when you meant to delete 300. */}
          <Alert
            severity={scope === 'all' ? 'warning' : 'info'}
            sx={{ mb: 2 }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Scope
            </Typography>
            <Typography variant="body2">
              {scope === 'all' ? (
                <>
                  Every entity in the database (no entity filter).
                  Affects <strong>{hardDeleteOpen === 'tracks'
                    ? (preview?.tracks?.by_entity?.length ?? '?')
                    : (preview?.dwells?.by_entity?.length ?? '?')}</strong>{' '}
                  distinct entities. Top:{' '}
                  {(hardDeleteOpen === 'tracks'
                    ? preview?.tracks?.by_entity
                    : preview?.dwells?.by_entity
                  )?.slice(0, 3).map((b) => `${b.entity_id} (${b.count.toLocaleString()})`).join(', ') || '(unknown)'}
                </>
              ) : (
                <>
                  Restricted to {selectedEntityIds.length} loaded{' '}
                  {selectedEntityIds.length === 1 ? 'entity' : 'entities'}:{' '}
                  <strong>
                    {selectedEntityIds.slice(0, 3).join(', ')}
                    {selectedEntityIds.length > 3 && ` + ${selectedEntityIds.length - 3} more`}
                  </strong>
                </>
              )}
            </Typography>
          </Alert>

          <DialogContentText sx={{ mb: 2 }}>
            Type <strong>DELETE</strong> (uppercase) to confirm.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            size="small"
            value={hardDeleteConfirm}
            onChange={(e) => setHardDeleteConfirm(e.target.value)}
            placeholder="Type DELETE"
            error={hardDeleteConfirm.length > 0 && hardDeleteConfirm !== 'DELETE'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setHardDeleteOpen(false); setHardDeleteConfirm(''); }}>
            Cancel
          </Button>
          <Button
            onClick={handleHardDelete}
            color="error"
            variant="contained"
            disabled={hardDeleteConfirm !== 'DELETE' || actionLoading}
          >
            {actionLoading ? <CircularProgress size={18} /> : 'Permanently delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
